import React, { useMemo, useState } from "react";

import { formatCurrency } from "../../../../../../../../utilities/helpers";

import Button from "react-bootstrap/Button";

import { DroppableItem } from "../../DroppableItem";
import { Item } from "../../Item";

export default function PriceLevel({
  canEdit,
  level,
  levels,
  setLevels,
  handleShow,
  sumRevenue,
  handleRemove,
  getObjectTotalSeatCount,
  isDroppableInSelected,
}) {
  const { id, price, name, color } = level;

  const [hasHover, setHasHover] = useState(false);
  const [nameHover, setNameHover] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(name || "");
  const [nameError, setNameError] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);

  const COLOR_OPTIONS = useMemo(
    () => [
      "#F032E6",
      "#F58231",
      "#FFE119",
      "#E6194B",
      "#DCBEFF",
      "#800000",
      "#FFD8B1",
      "#3CB44B",
      "#BFEF45",
      "#000075",
      "#9A6324",
      "#42D4F4",
      "#AAFFC3",
      "#808000",
      "#911EB4",
      "#469990",
      "#FABED4",
      "#A9A9A9",
    ],
    [],
  );

  const validateName = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Name is required.";
    }
    if (trimmed.length > 30) {
      return "Name must be 30 characters or fewer.";
    }
    return "";
  };

  const commitNameChange = () => {
    if (!canEdit || !setLevels) return;
    const error = validateName(tempName);
    setNameError(error);
    if (error) return;

    setLevels((prevState) => {
      if (!prevState?.[id]) return prevState;
      return {
        ...prevState,
        [id]: {
          ...prevState[id],
          name: tempName.trim(),
        },
      };
    });
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitNameChange();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setTempName(name || "");
      setIsEditingName(false);
      setNameError("");
    }
  };

  const handleColorSelect = (hex) => {
    if (!canEdit || !setLevels) return;
    setLevels((prevState) => {
      if (!prevState?.[id]) return prevState;
      return {
        ...prevState,
        [id]: {
          ...prevState[id],
          color: hex,
        },
      };
    });
    setShowColorPicker(false);
  };

  const renderName = ({ el, isSubItem, size, weight }) => {
    const displayName = isEditingName ? tempName : el.name;
    return (
      <div
        className={`flex flex-sm ${isSubItem ? "sub-item-name-container" : ""}`}
        style={{ position: "relative" }}
      >
        <button
          type="button"
          className={`dot ${isSubItem ? "dot-sm" : ""}`}
          style={{
            backgroundColor: color,
            border: "none",
            padding: 0,
            cursor: canEdit ? "pointer" : "default",
          }}
          disabled={!canEdit}
          onClick={(e) => {
            e.stopPropagation();
            if (!canEdit) return;
            setShowColorPicker((prev) => !prev);
          }}
        ></button>
        <span
          className={`item-name ${size ? `item-name-${size}` : ""} ${
            weight ? `item-name-${weight}` : ""
          }`}
          onClick={() => {
            if (!canEdit) return;
            setIsEditingName(true);
            setTempName(el.name || "");
            setNameError("");
          }}
          onMouseEnter={() => setNameHover(true)}
          onMouseLeave={() => setNameHover(false)}
          style={{
            cursor: canEdit ? "text" : "default",
            ...(canEdit && nameHover && !isEditingName
              ? { color: "#3E8BF7", textDecoration: "underline" }
              : {}),
          }}
        >
          {isEditingName ? (
            <input
              type="text"
              className="form-control form-control-sm"
              value={tempName}
              autoFocus
              onChange={(e) => {
                setTempName(e.target.value);
                if (nameError) {
                  setNameError(validateName(e.target.value));
                }
              }}
              onBlur={commitNameChange}
              onKeyDown={handleNameKeyDown}
            />
          ) : (
            displayName
          )}
        </span>
        {nameError && (
          <span className="text-danger small ms-2">{nameError}</span>
        )}
        {showColorPicker && canEdit && (
          <div
            className="bg-white border rounded shadow-sm p-2"
            style={{
              position: "absolute",
              top: "120%",
              left: 0,
              zIndex: 20,
              minWidth: "160px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex flex-wrap gap-1 mb-2">
              {COLOR_OPTIONS.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className="border-0"
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: hex,
                    cursor: "pointer",
                  }}
                  onClick={() => handleColorSelect(hex)}
                />
              ))}
            </div>
            <div className="d-flex align-items-center gap-2">
              <input
                type="color"
                className="form-control form-control-color p-0"
                value={color}
                onChange={(e) => handleColorSelect(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => setShowColorPicker(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DroppableItem
      index={id.toString()}
      data={levels}
      hasNoPrice={!level.price}
      styles="d-flex flex-column gap-2"
      hasHover={canEdit}
      isDroppableInSelected={isDroppableInSelected}
    >
      <Item
        index={id}
        entity="level"
        el={level}
        data={levels}
        canRemove={Boolean(handleRemove) && canEdit}
        handleRemove={handleRemove}
        getObjectTotalSeatCount={getObjectTotalSeatCount}
        renderName={renderName}
      >
        <div className="split-row">
          <div className="flex gap-4">
            <span>Base price </span>
            <Button
              variant="link"
              onMouseEnter={() => setHasHover(true)}
              onMouseLeave={() => setHasHover(false)}
              onClick={(e) => handleShow(e, level)}
              disabled={!canEdit}
            >
              {price ? (
                <>
                  <span
                    style={{ display: `${hasHover ? "none" : "block"}` }}
                    className="text-body"
                  >
                    {formatCurrency(price)}
                  </span>
                  <span
                    style={{ display: `${hasHover ? "block" : "none"}` }}
                    className="text-link text-link--thin"
                  >
                    Edit value
                  </span>
                </>
              ) : (
                <span className="text-link text-link--thin">Add value</span>
              )}
            </Button>
          </div>
          <span>
            {price ? formatCurrency(sumRevenue(level)) : formatCurrency(0)}{" "}
          </span>
        </div>
      </Item>
    </DroppableItem>
  );
}
